import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Select } from 'components/forms/Selects'
import { Checkbox } from 'transfix-ui/components/Checkbox'
import { ICarrierState, TCarrierStatus } from 'types/carrier'
import { updateCarrierStatus, closeCarrierStatusModal } from 'utils/actions/carrier'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { IStore } from 'apps/carrier/utils/reducers'
import { useMPAnalytics } from 'apps/marketplace/hooks/useMPAnalytics'
import { FormModal } from 'transfix-ui'
import { CARRIER_STATUSES } from 'constants/carriers'
import { NeedPermissionModal } from '../../NeedPermissionModal'

interface IOption {
  label: string
  value: TCarrierStatus
}
const options: IOption[] = CARRIER_STATUSES.map(({ key, name }) => ({ label: name, value: key }))

export const CarrierStatusModal = () => {
  const { trackEvent } = useMPAnalytics()
  const dispatch = useDispatch()
  const {
    statusModal,
    isFetching,
    error,
    carrierDetails: { id = '', state = '' } = {},
  } = useSelector<IStore, ICarrierState>(store => ({
    ...store.carrier,
  }))
  const [selectedState, setSelectedState] = useState<IOption>()

  const [isUserValidationChecked, setIsUserValidationChecked] = useState<boolean>(false)

  const { userPermissions } = useUserDetails()
  const editPermitted = userPermissions?.canEditCarrierStatus
  useEffect(() => {
    if (statusModal) setSelectedState(options.find(entry => entry.value === state))
  }, [statusModal])

  const userValidationCheckEnabled = useFeatureFlag('checkIsUserValidationChecked')

  const forceClose = () => dispatch(closeCarrierStatusModal())
  const modalHandler = (action?: string) => {
    if (action === 'confirm') {
      dispatch(
        updateCarrierStatus(id, {
          newState: selectedState!.value,
          isUserValidationChecked,
        })
      )
    } else {
      setIsUserValidationChecked(false)
      forceClose()
    }
  }
  const selectHandler = (option: IOption) => setSelectedState(option)

  const checkboxHandler = () => {
    trackEvent('Carrier Application', 'CLICKED_USER_VALIDATION_CHECKBOX')
    setIsUserValidationChecked(!isUserValidationChecked)
  }

  const isSaveButtonDisabled = (): boolean => {
    if (
      (['new', 'pending', 'application_submitted'].includes(state) &&
        selectedState?.value === 'activated' &&
        !isUserValidationChecked) ||
      isFetching
    ) {
      return true
    }
    return false
  }

  return editPermitted ? (
    <FormModal
      error={error ? error.replace(/^(Error: )/, '').split(', ') : error}
      title='Edit Status'
      open={statusModal}
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          disabled: isFetching,
          dataTest: 'status-cancel',
        },
        {
          label: 'Save',
          type: 'confirm',
          disabled: userValidationCheckEnabled ? isSaveButtonDisabled() : isFetching,
          dataTest: 'status-save',
        },
      ]}
      onClose={modalHandler}>
      <div className='row'>
        <div className='col-lg-4 col-xs-6'>
          {userValidationCheckEnabled ? (
            <div>
              <Checkbox
                label='I have completed user validation'
                disabled={false}
                checked={isUserValidationChecked}
                onChange={checkboxHandler}
              />
            </div>
          ) : null}
          <Select label='Status' options={options} value={selectedState} onChange={selectHandler} />
        </div>
      </div>
    </FormModal>
  ) : (
    <NeedPermissionModal
      action='edit a carrier’s status'
      contact='Compliance'
      open={statusModal}
      onClose={forceClose}
    />
  )
}
